'use client';

import { forwardRef, useCallback, useId, useMemo, useState } from 'react';

import { css, cx } from '@/styled-system/css';

import { useControllableValue } from '../../hooks/use-controllable-value';
import { PhotoCarouselRootContext } from './context/photo-carousel-root.context';
import { photoCarouselRootStyles } from './styles/photo-carousel-root.styles';
import type { PhotoCarouselRootPropsInterface } from './types/photo-carousel-root.types';

/**
 * @since 10.0.0
 */
export const PhotoCarouselRoot = forwardRef<HTMLDivElement, PhotoCarouselRootPropsInterface>(
  (props: PhotoCarouselRootPropsInterface, forwardedRef) => {
    const {
      activeIndex,
      'aria-label': ariaLabel,
      children,
      className,
      css: cssProp,
      defaultActiveIndex = 0,
      onActiveIndexChange,
      shouldAwaitInteractionResponse = true,
      shouldLoop = true,
      ...rest
    } = props;
    const id = useId();

    const [activeIndexState, setActiveIndexState] = useControllableValue<number>({
      controlledValue: activeIndex ?? 0,
      uncontrolledValue: defaultActiveIndex,
      isControlled: 'activeIndex' in props,
      isUncontrolled: 'defaultActiveIndex' in props,
      onChange: onActiveIndexChange,
      componentName: 'PhotoCarousel.Root',
      controlledPropName: 'activeIndex',
      uncontrolledPropName: 'defaultActiveIndex',
    });

    // totalSlides is calculated by `PhotoCarousel.Slides`
    const [totalSlides, setTotalSlides] = useState<number>(0);

    const styles = css(photoCarouselRootStyles, cssProp);

    // Takes a new slide index from an event handler and
    // decides whether it needs to be adjusted based on `shouldLoop`.
    // Also runs consumer's `onActiveIndexChange` callback.
    const updateActiveIndex = useCallback(
      (index: number) => {
        let newIndex = index;

        if (index > totalSlides - 1) {
          newIndex = shouldLoop ? 0 : totalSlides - 1;
        } else if (index < 0) {
          newIndex = shouldLoop ? totalSlides - 1 : 0;
        }

        if (activeIndexState !== newIndex) {
          setActiveIndexState(newIndex);
        }
      },
      [activeIndexState, shouldLoop, totalSlides, setActiveIndexState],
    );

    const contextValue = useMemo(
      () => ({
        activeIndex: activeIndexState,
        id,
        shouldLoop,
        setTotalSlides,
        shouldAwaitInteractionResponse,
        totalSlides,
        updateActiveIndex,
      }),
      [
        activeIndexState,
        id,
        shouldLoop,
        setTotalSlides,
        shouldAwaitInteractionResponse,
        totalSlides,
        updateActiveIndex,
      ],
    );

    return (
      <PhotoCarouselRootContext.Provider value={contextValue}>
        <section
          aria-label={ariaLabel}
          className={cx('c11n', 'c11n_photo-carousel__root', styles, className)}
          data-c11n-component="PhotoCarousel.Root"
          data-c11n-version={__C11N_VERSION__}
          ref={forwardedRef}
          {...rest}
        >
          {children}
        </section>
      </PhotoCarouselRootContext.Provider>
    );
  },
);

PhotoCarouselRoot.displayName = 'PhotoCarousel.Root';
